import React, { useMemo } from "react"

import { useKnowledgeBaseEntriesTransport } from "@/agents/knowledge-base"
import { Badge } from "@coval/ui"

import { InlineDiff } from "@/components/diff/InlineDiff"
import type { AttachedKnowledgeBaseEntry } from "@/types/api/TestCaseRecord"

import { useTestSetEditorContext } from "../../../contexts/TestSetEditorContext"
import type { EditableTestCase } from "../../../model"
import { getPendingEditForTestCase } from "../../../utils/pendingChangesValidation"

interface KnowledgeBaseEntriesProps {
  testCase: EditableTestCase
  isExpanded: boolean
}

/** Read-only display of a test case's knowledge-base attachments; entries are historical and can no longer be added, changed, or removed. */
export const KnowledgeBaseEntries: React.FC<KnowledgeBaseEntriesProps> = ({
  testCase,
  isExpanded,
}) => {
  const { pendingChanges, activeAgentId } = useTestSetEditorContext()
  const pendingEdit = getPendingEditForTestCase(testCase.id, pendingChanges)

  const { data: availableKbEntries = [], isLoading: isLoadingKbEntries } =
    useKnowledgeBaseEntriesTransport(activeAgentId, {
      enabled: !!activeAgentId,
    })

  const kbEntries = useMemo(
    () => testCase.connected_knowledge_base_entries || [],
    [testCase.connected_knowledge_base_entries],
  )

  // Check if an entry is unavailable to the current active agent
  const isEntryUnavailable = useMemo(() => {
    const unavailableSet = new Set<string>()
    if (!activeAgentId) {
      kbEntries.forEach((entry) => {
        unavailableSet.add(entry.id)
      })
    } else if (!isLoadingKbEntries) {
      const availableIds = new Set(availableKbEntries.map((e) => e.id))
      kbEntries.forEach((entry) => {
        if (!availableIds.has(entry.id)) {
          unavailableSet.add(entry.id)
        }
      })
    }
    return (entryId: string) => unavailableSet.has(entryId)
  }, [activeAgentId, availableKbEntries, kbEntries, isLoadingKbEntries])

  const pendingKbEntries = pendingEdit?.connected_knowledge_base_entries

  // Collapsed view
  if (!isExpanded) {
    if (kbEntries.length === 0) {
      return null
    }

    return (
      <div className="flex flex-wrap gap-1 w-full min-w-0 items-center">
        {kbEntries.slice(0, 2).map((entry) => {
          const kbEntry = availableKbEntries.find((e) => e.id === entry.id)
          const displayName = kbEntry?.name || entry.id
          const unavailable = isEntryUnavailable(entry.id)
          return (
            <Badge
              key={entry.id}
              variant={unavailable ? "danger" : "info"}
              className="max-w-[180px] shrink-0 truncate"
              title={
                unavailable ? "Entry unavailable to this agent" : displayName
              }
            >
              {unavailable ? "Entry unavailable to this agent" : displayName}
            </Badge>
          )
        })}
        {kbEntries.length > 2 && (
          <Badge variant="info" className="shrink-0 font-medium">
            +{kbEntries.length - 2} more
          </Badge>
        )}
      </div>
    )
  }

  // Expanded view
  const displayEntries = pendingKbEntries || kbEntries
  if (displayEntries.length === 0) {
    return (
      <span className="text-sm text-coval-text-muted select-none">
        No KB entries
      </span>
    )
  }

  return (
    <div className="flex flex-col gap-2 w-full">
      {(pendingKbEntries || kbEntries).map(
        (entry: AttachedKnowledgeBaseEntry) => {
          const oldEntry = kbEntries.find((e) => e.id === entry.id)
          const newEntry = pendingKbEntries?.find((e) => e.id === entry.id)
          const showDiff = pendingKbEntries !== undefined
          const displayEntry = newEntry || entry

          const kbEntry = availableKbEntries.find(
            (e) => e.id === displayEntry.id,
          )
          const displayName = kbEntry?.name || displayEntry.id
          const unavailable = !showDiff && isEntryUnavailable(displayEntry.id)

          return (
            <div key={displayEntry.id} className="flex gap-2 items-start">
              {showDiff ? (
                <div className="flex-1 text-sm whitespace-pre-wrap outline-none bg-coval-info-surface text-coval-info-on-surface rounded p-2 min-h-8">
                  <InlineDiff
                    oldText={oldEntry?.id || ""}
                    newText={newEntry?.id || ""}
                  />
                </div>
              ) : (
                <div
                  className={`flex-1 rounded p-2 min-h-8 text-sm ${
                    unavailable
                      ? "bg-coval-danger-surface text-coval-danger-on-surface"
                      : "bg-coval-info-surface text-coval-info-on-surface"
                  }`}
                >
                  {unavailable
                    ? "Entry unavailable to this agent"
                    : displayName}
                </div>
              )}
            </div>
          )
        },
      )}
    </div>
  )
}
